import React, { createContext, useContext, useEffect, useState, useCallback, useMemo } from "react";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STORAGE_KEY = "md_currency_v1";
const GEO_DONE_KEY = "md_currency_geo_v1"; // marker: geo detection already ran this browser

const SYMBOLS = { EUR: "€", USD: "$", GBP: "£" };
const LOCALE = { EUR: "fr-FR", USD: "en-US", GBP: "en-GB" };
const SUPPORTED = ["EUR", "USD", "GBP"];

// Sensible defaults so UI never blocks on initial load
const DEFAULT_RATES = { EUR: 1, USD: 1.08, GBP: 0.86 };

const CurrencyContext = createContext(null);

// Detect an initial currency from browser locale (only used the first time)
function detectInitial() {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (stored && SUPPORTED.includes(stored)) return stored;
    const lang = (navigator.language || "").toLowerCase();
    if (lang.startsWith("en-us") || lang.startsWith("en-ca")) return "USD";
    if (lang.startsWith("en-gb") || lang.startsWith("en-uk")) return "GBP";
    return "EUR";
  } catch {
    return "EUR";
  }
}

export const CurrencyProvider = ({ children }) => {
  const [currency, setCurrencyState] = useState(detectInitial);
  const [rates, setRates] = useState(DEFAULT_RATES);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await axios.get(`${API}/currency/rates`);
        if (data?.rates) setRates((prev) => ({ ...prev, ...data.rates }));
      } catch {
        /* keep defaults */
      }
    })();
  }, []);

  // Geo-IP currency detection: runs once per browser, only when the user has NOT
  // manually chosen a currency yet (no STORAGE_KEY set). Never overrides a manual choice.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const manuallyChosen = localStorage.getItem(STORAGE_KEY);
        const geoDone = localStorage.getItem(GEO_DONE_KEY);
        if (manuallyChosen || geoDone) return;
        const { data } = await axios.get(`${API}/currency/geo`);
        if (cancelled) return;
        localStorage.setItem(GEO_DONE_KEY, "1");
        if (data?.currency && SUPPORTED.includes(data.currency)) {
          setCurrencyState(data.currency);
        }
      } catch {
        /* silently ignore — keep locale-based default */
      }
    })();
    return () => { cancelled = true; };
  }, []);

  const setCurrency = useCallback((c) => {
    if (!SUPPORTED.includes(c)) return;
    setCurrencyState(c);
    try {
      localStorage.setItem(STORAGE_KEY, c);
      localStorage.setItem(GEO_DONE_KEY, "1"); // lock in the manual choice
    } catch { /* noop */ }
  }, []);

  const formatPrice = useCallback(
    (eurAmount, opts = {}) => {
      const rate = rates[currency] || 1;
      const converted = Number(eurAmount) * rate;
      const decimals = opts.decimals ?? 2;
      try {
        return new Intl.NumberFormat(LOCALE[currency] || "fr-FR", {
          style: "currency",
          currency,
          minimumFractionDigits: decimals,
          maximumFractionDigits: decimals,
        }).format(converted);
      } catch {
        return `${converted.toFixed(decimals)}${SYMBOLS[currency] || currency}`;
      }
    },
    [currency, rates]
  );

  const value = useMemo(
    () => ({ currency, setCurrency, rates, formatPrice, symbol: SYMBOLS[currency], supported: SUPPORTED }),
    [currency, rates, setCurrency, formatPrice]
  );

  return <CurrencyContext.Provider value={value}>{children}</CurrencyContext.Provider>;
};

export const useCurrency = () => useContext(CurrencyContext);
